// Lycan — menu, pen draw-on, forms, floating header. No dependencies.

/* ---------- NEXT FREE SELF DEFENSE WORKSHOP ----------
   The one line to edit each round. Set the date (and time, if you want it shown):
     const NEXT_WORKSHOP = "2026-11-15T10:30";
   Leave it "" between workshops. The site switches back off by itself the day after.
   On Shopify, set it in the theme editor instead: Theme settings → Self Defense Workshop. */
const NEXT_WORKSHOP = window.LYCAN?.nextWorkshop || "";

const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ---------- menu ---------- */
function initMenu() {
  const btn = document.querySelector(".menu-btn"), nav = document.getElementById("nav");
  if (!btn || !nav) return;
  const set = open => {
    nav.classList.toggle("open", open);
    btn.setAttribute("aria-expanded", String(open));
    btn.textContent = open ? "Close" : "Menu";
    document.body.style.overflow = open ? "hidden" : "";
    document.querySelectorAll("main, footer").forEach(el => el.inert = open);
    if (open) nav.querySelector("a")?.focus();
  };
  btn.addEventListener("click", () => set(btn.getAttribute("aria-expanded") !== "true"));
  addEventListener("keydown", e => { if (e.key === "Escape" && nav.classList.contains("open")) { set(false); btn.focus(); } });
  nav.addEventListener("click", e => { if (e.target.closest("a")) set(false); });
  matchMedia("(min-width: 961px)").addEventListener("change", e => e.matches && set(false));
}

/* ---------- pen draw-on (empty slots) ----------
   A hand-drawn rectangle, built at the slot's real pixel size so the stroke never distorts.
   Corners overshoot and the lid doesn't quite close, like a ballpoint outline. */
function penPath(w, h) {
  const i = 12, j = (n) => n * (Math.min(w, h) / 300); // wobble scales with slot size
  return `M${i + j(4)} ${i + j(2)} C${w * .35} ${i - j(3)} ${w * .7} ${i + j(4)} ${w - i + j(3)} ${i - j(2)}` +
    ` C${w - i + j(4)} ${h * .35} ${w - i - j(3)} ${h * .7} ${w - i + j(2)} ${h - i + j(3)}` +
    ` C${w * .65} ${h - i + j(5)} ${w * .3} ${h - i - j(2)} ${i - j(2)} ${h - i + j(4)}` +
    ` C${i + j(3)} ${h * .65} ${i - j(3)} ${h * .3} ${i + j(1)} ${i + j(14)} L${i + j(12)} ${i - j(1)}`;
}
function initPen() {
  const pens = [...document.querySelectorAll(".slot svg.pen")];
  const size = svg => {
    const { width: w, height: h } = svg.getBoundingClientRect();
    if (w < 2) return;
    svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
    const path = svg.querySelector("path");
    path.setAttribute("d", penPath(w, h));
    svg.style.setProperty("--len", Math.ceil(path.getTotalLength()) + 1);
  };
  const ro = new ResizeObserver(es => es.forEach(e => size(e.target)));
  pens.forEach(p => { size(p); ro.observe(p); });
  if (reduced || !("IntersectionObserver" in window)) return;
  const io = new IntersectionObserver(es => es.forEach(e => {
    if (!e.isIntersecting) return;
    e.target.classList.add("draw"); io.unobserve(e.target);
  }), { threshold: 0.5 });
  pens.forEach(p => { p.classList.add("armed"); io.observe(p); });
}

/* ---------- fallback Shopify contact forms: friendly validation, no double submits ---------- */
function initForms() {
  document.querySelectorAll("form.form, form.form-inline").forEach(form => {
    form.noValidate = true; // our messages, not the browser's
    form.addEventListener("submit", e => {
      let ok = true;
      form.querySelectorAll("[required]").forEach(f => {
        const err = form.querySelector(`#${f.id}-err`);
        const bad = !f.value.trim() || (f.type === "email" && !/^\S+@\S+\.\S+$/.test(f.value));
        f.setAttribute("aria-invalid", String(bad));
        if (err) err.textContent = bad ? (f.type === "email" ? "Enter an email we can reply to, like name@example.com." : "Enter your name so we know who to reply to.") : "";
        if (bad && ok) { f.focus(); ok = false; }
      });
      const btn = form.querySelector("[type=submit]");
      if (!ok || btn.disabled) { e.preventDefault(); return; } // invalid, or already sending
      btn.disabled = true; setTimeout(() => (btn.disabled = false), 4000);
    });
  });
}

/* ---------- workshop slideshow: advances every 5s, with pause; still by default under reduced motion ---------- */
function initSlideshow() {
  const show = document.querySelector(".slideshow");
  if (!show) return;
  const slides = [...show.querySelectorAll(".slide")];
  const pause = show.querySelector('[data-slide="pause"]');
  let i = 0, timer = null;
  const go = n => {
    slides[i].classList.remove("is-active");
    i = (n + slides.length) % slides.length;
    slides[i].classList.add("is-active");
    slides.forEach((s, k) => s.setAttribute("aria-hidden", String(k !== i)));
  };
  const play = on => {
    clearInterval(timer); timer = on ? setInterval(() => go(i + 1), 5000) : null;
    pause.textContent = on ? "Pause" : "Play";
  };
  show.querySelector('[data-slide="prev"]').onclick = () => { go(i - 1); if (timer) play(true); };
  show.querySelector('[data-slide="next"]').onclick = () => { go(i + 1); if (timer) play(true); };
  pause.onclick = () => play(!timer);
  show.querySelector(".slide-controls").hidden = false;
  go(0);
  play(!matchMedia("(prefers-reduced-motion: reduce)").matches);
}

/* ---------- home: header floats over the photo, turns solid once you scroll ---------- */
function initMasthead() {
  const head = document.querySelector(".home .masthead");
  if (!head) return;
  const set = () => head.classList.toggle("solid", scrollY > 40);
  addEventListener("scroll", set, { passive: true }); set();
}

/* ---------- GymDesk schedule: loading, loaded, or failed ----------
   The widget script builds an iframe inside .gymdesk-schedule. If none appears within
   10s (blocked by an ad blocker, GymDesk down, offline), show the fallback. */
function initSchedule() {
  const box = document.querySelector(".schedule-embed");
  if (!box) return;
  const target = box.querySelector(".gymdesk-schedule");
  const done = () => {
    const frame = target.querySelector("iframe");
    if (!frame) return false;
    frame.title = "Lycan Jiu Jitsu class schedule";
    box.dataset.state = "loaded";
    return true;
  };
  const mo = new MutationObserver(() => done() && mo.disconnect());
  mo.observe(target, { childList: true, subtree: true });
  setTimeout(() => { if (!done()) { mo.disconnect(); box.dataset.state = "failed"; } }, 10000);
  addEventListener("offline", () => { if (box.dataset.state === "loading") box.dataset.state = "failed"; });
}

/* ---------- GymDesk forms: name the iframe the widget injects, for screen readers ---------- */
function initFormTitles() {
  document.querySelectorAll(".maonrails-form").forEach(box => {
    const mo = new MutationObserver(() => {
      const frame = box.querySelector("iframe");
      if (frame) { frame.title = "Lycan Jiu Jitsu enquiry form"; mo.disconnect(); }
    });
    mo.observe(box, { childList: true, subtree: true });
    // no form after 10s (ad blocker, GymDesk down, offline): say so, like the schedule does
    setTimeout(() => {
      if (box.querySelector("iframe")) return;
      mo.disconnect();
      box.hidden = true; // its empty paper panel
      box.insertAdjacentHTML("afterend", '<div class="embed-note form-failed" role="status"><p class="title">The form didn\'t load.</p>' +
        '<p>It comes from GymDesk, and an ad blocker or a weak connection can stop it. Reload the page, or message us on ' +
        '<a href="https://www.instagram.com/lycanjiujitsu">Instagram</a> or email <a href="mailto:lycanjiujitsu@gmail.com">lycanjiujitsu@gmail.com</a>.</p></div>');
    }, 10000);
  });
}

/* ---------- workshop: show "upcoming" or "none" pieces ----------
   Markup ships in the "none" state, so the site is right without JS or with no date set. */
function initWorkshopDate() {
  const hasTime = NEXT_WORKSHOP.length > 10;
  const when = NEXT_WORKSHOP ? new Date(hasTime ? NEXT_WORKSHOP : NEXT_WORKSHOP + "T00:00") : null;
  const endOfDay = when && new Date(when.getFullYear(), when.getMonth(), when.getDate(), 23, 59, 59);
  const upcoming = !!(when && !isNaN(when) && endOfDay >= new Date());
  document.querySelectorAll("[data-when]").forEach(el => { el.hidden = (el.dataset.when === "upcoming") !== upcoming; });
  if (!upcoming) return;
  const day = { weekday: "short", month: "short", day: "numeric" };
  const short = when.toLocaleDateString("en-US", day);
  const long = when.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) +
    (hasTime ? " · " + when.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "");
  document.querySelectorAll("[data-ws-date]").forEach(el => { el.textContent = el.dataset.wsDate === "long" ? long : short; });
}

/* ---------- hero video ----------
   Wide screens only: phones crop a landscape clip badly and pay for the data. Honors reduced
   motion and data-saver. The photo stays underneath, so any failure just leaves the photo. */
function initHeroVideo() {
  const v = document.querySelector(".hero-video");
  if (!v) return;
  if (reduced || navigator.connection?.saveData || !matchMedia("(min-width: 721px)").matches) { v.remove(); return; }
  const btn = document.querySelector(".video-toggle"), cap = document.querySelector("[data-video-cap]");
  v.addEventListener("playing", () => {
    v.classList.add("on");
    if (cap) cap.textContent = cap.dataset.videoCap;
    if (btn) btn.hidden = false;
  }, { once: true });
  btn?.addEventListener("click", () => {
    if (v.paused) { v.play(); btn.textContent = "Pause"; } else { v.pause(); btn.textContent = "Play"; }
  });
  v.src = v.dataset.src;
  v.play().catch(() => v.remove());
}

initHeroVideo(); initWorkshopDate(); initSlideshow(); initMenu(); initPen(); initForms(); initMasthead(); initSchedule(); initFormTitles();
